// AHU 공기 상태 계산 함수들
// VBA ahucode3.bas에서 변환

import { PsychrometricCalculator } from './psychrometric';
import { rtsPwfucLookupData } from './dataManager';

// ASHRAE Temperature calculation function (VBA basiscode.bas에서 변환)
function calculateASHRAETemperature(hour: number, peakTemp: number, dailyRange: number): number {
  const aCoef = [0.562917, 0.29319, -0.03482, -0.00059, -0.00167, 0.00128,
                 0, 0.00044, -0.00167, 0.00059, -0.00018, 0.00009];
  const bCoef = [0.3848108, -0.0835246, -0.0005893, 0, -0.0004341, -0.0008333,
                 -0.0009651, 0, -0.0005893, 0.0001912, 0.0003418];
  
  // VBA와 동일한 방식으로 π 계산: pi = 4 * Atn(1) = 4 * arctan(1)
  const pi = 4 * Math.atan(1);
  let temperature = aCoef[0];
  
  // Hour normalization (VBA logic)
  if (hour < 0) hour = hour + 24;
  
  
  for (let i = 1; i <= 11; i++) {
    const cosValue = Math.cos(2 * pi * i * hour / 24);
    const sinValue = Math.sin(2 * pi * i * hour / 24);
    const cosTerm = aCoef[i] * cosValue;
    const sinTerm = bCoef[i - 1] * sinValue;
    temperature = temperature + cosTerm + sinTerm;
    
  }
  
  const result = peakTemp - dailyRange * temperature;
  
  return result;
}

// VBA designcode.bas의 시간별 외기조건 계산 로직 구현
export function calculateHourlyOutdoorCondition(
  hour: number, 
  baseTemp: number, 
  baseHumidity: number, 
  dailyRange: number, 
  altitude: number = 0
): { temperature: number; humidity: number; humidityRatio: number } {
  // 1. ASHRAE 공식으로 시간별 온도 계산
  const hourlyTemp = calculateASHRAETemperature(hour, baseTemp, dailyRange);
  
  // 2. 기본 조건에서 습도비 계산 (이 값이 고정값으로 사용됨)
  const baseHumidityRatio = PsychrometricCalculator.calculateHumidityRatio(baseTemp, baseHumidity, altitude);
  
  // 3. 시간별 온도와 고정 습도비로 상대습도 계산
  let hourlyHumidity = PsychrometricCalculator.calculateRelativeHumidity(hourlyTemp, baseHumidityRatio, altitude);
  
  // 4. VBA 로직: 상대습도가 95%를 초과하면 95%로 제한하고 습도비를 재계산
  if (hourlyHumidity > 95) {
    const adjustedHumidityRatio = PsychrometricCalculator.calculateHumidityRatio(hourlyTemp, 95, altitude);
    return { 
      temperature: Math.round(hourlyTemp * 100) / 100, 
      humidity: 95, 
      humidityRatio: Math.round(adjustedHumidityRatio * 10000) / 10000 
    };
  }
  
  return { 
    temperature: Math.round(hourlyTemp * 100) / 100, 
    humidity: Math.round(hourlyHumidity * 100) / 100, 
    humidityRatio: Math.round(baseHumidityRatio * 10000) / 10000 
  };
}

export interface AirState {
  dryBulbTemp: number;      // 건구온도 (°C)
  relativeHumidity: number; // 상대습도 (%)
  humidityRatio: number;    // 절대습도 (kg/kg)
  enthalpy: number;         // 엔탈피 (kJ/kg)
}

export interface AHUInputs {
  // 냉방 실내 조건
  roomTemp: number;           // 냉방 실내온도 (°C)
  roomHumidity: number;       // 냉방 실내습도 (%)

  // 난방 실내 조건
  heatingRoomTemp?: number;   // 난방 실내온도 (°C)
  heatingRoomHumidity?: number; // 난방 실내습도 (%)

  // 냉방 외기 조건
  outdoorTemp: number;        // 냉방 외기온도 (°C)
  outdoorHumidity: number;    // 냉방 외기습도 (%)

  // 난방 외기 조건
  heatingOutdoorTemp?: number;    // 난방 외기온도 (°C)
  heatingOutdoorHumidity?: number; // 난방 외기습도 (%)

  altitude?: number;          // 고도 (m) - ASHRAE 계산용
  
  // 풍량 조건
  supplyAirflow: number;      // 급기량 (CMH)
  outdoorAirflow: number;     // 외기량 (CMH)
  
  // 공간 정보
  conditionedArea?: number;   // 공조면적 (m²)
  ceilingHeight?: number;     // 층고 (m)
  
  // 냉방 부하 조건
  sensibleHeat: number;       // 냉방 현열부하 (W)
  latentHeat: number;         // 냉방 잠열부하 (W)

  // 난방 부하 조건
  heatingLoad?: number;       // 난방 부하 (W)
  
  // 장비 조건
  coolingCoilTemp: number;    // 냉각코일 온도차 (°C) - VBA CCdb, 급기 온도차 계산용
  supplyTempDelta?: number;   // 급기 온도차 (°C) - 사용자 입력값, 에러 메시지 표시용
  saFanTemp: number;          // SA 팬 온도상승 (°C) - 하위 호환용, 동적 계산으로 대체됨
  rhReheatTemp: number;       // 재열 온도상승 (°C)
  preheatingTemp?: number;    // 예열코일 온도상승 (°C)
  
  // 팬 설정 (동적 SAte 계산용)
  hasFan?: boolean;           // 팬 유무
  fanType?: string;           // 팬 타입: 'Draw(I)' | 'Draw(O)' | 'Blow(I)' | 'Blow(O)'
  fanEfficiency?: number;     // 팬 효율 (%)
  fanPressure?: number;       // 팬 압력 (Pa)
  motorEfficiency?: number;   // 모터 효율 (%)
  motorSafetyFactor?: number; // 모터 안전율 (%)
  sfLoadPercentage?: number; // SF부하 적용 비율 (%) - VBA TextBoxfn1
  rfLoadPercentage?: number; // RF부하 적용 비율 (%)
  
  // 배열 회수기 설정 (VBA 원본 반영)
  heatRecoveryType?: 'None' | 'S.H' | 'T.H'; // 배열 회수기 타입 (DDHR)
  coolingRecoveryEfficiency?: number;         // 냉방용 회수 효율 (%) (TextBox6)
  heatingRecoveryEfficiency?: number;         // 난방용 회수 효율 (%) (TextBox7)
  
  // 코일 타입 (VBA DDCC, DDPH, DDRH와 매핑)
  coolingCoilType?: string;   // 냉각코일 타입: 'None' | 'Water' | 'DX'
  coolingInletTemp?: number;  // 냉수 입구 온도 (°C) - VBA TextBox10
  coolingOutletTemp?: number; // 냉수 출구 온도 (°C) - VBA TextBox11
  preheatingCoilType?: string; // 예열코일 타입: 'None' | 'Water' | 'Steam' | 'Elec'
  preheatingInletTemp?: number; // 예열 코일 입구 온도 (°C)
  preheatingOutletTemp?: number; // 예열 코일 출구 온도 (°C)
  reheatingCoilType?: string; // 재열코일 타입: 'None' | 'Water' | 'Steam' | 'Elec'
  reheatingInletTemp?: number; // 재열 코일 입구 온도 (°C)
  reheatingOutletTemp?: number; // 재열 코일 출구 온도 (°C)
  heatingCoilType?: string;   // 가열코일 타입: 'None' | 'Water' | 'Steam' | 'Elec'
  heatingInletTemp?: number;  // 난방 코일 입구 온도 (°C)
  heatingOutletTemp?: number; // 난방 코일 출구 온도 (°C)
  
  // RA Lighting Load 설정
  raLightingLoadMode?: 'included' | 'separate' | 'no'; // RA Lighting Load 모드
  ventilationLightingLoad?: number; // 환기 조명부하 (W)
  
  // 환기량 (RA 온도상승 계산용)
  returnAirflow?: number;     // 환기량 (CMH)
  
  // 시간별 온도 계산용 (ASHRAE)
  currentHour?: number;       // 현재 시간 (1-24)
  peakTemperature?: number;   // 최고온도 (°C) - rmbook1.Range("k3")
  dailyRange?: number;        // 일교차 (°C) - rmbook1.Range("o3")
  
  // 외기 적용 시각 설정
  outdoorTimeApplication?: 'standard' | 'peak';  // 외기 적용 시각 방식 ('표준 적용' 또는 '피크 시각 적용')
  outdoorApplicationTime?: string;  // 피크 시각 적용 시 사용할 시각 (시)
  
  // 기타
  elevation?: number;         // 고도 (m)
  hasHeatingCoil?: boolean;   // 난방코일 유무
  hasCoolingCoil?: boolean;   // 냉방코일 유무
}

export interface AHUResults {
  // 냉방 공기 상태들
  outdoorAir: AirState;       // 외기 상태 (OA)
  returnAir: AirState;        // 환기 상태 (RA)
  mixedAir: AirState;         // 혼합공기 상태 (MA)
  supplyAir: AirState;        // 급기 상태 (SA)

  // 난방 공기 상태들
  heatingOutdoorAir?: AirState;  // 난방 외기 상태 (o2)
  heatingReturnAir?: AirState;   // 난방 환기 상태 (r2)
  heatingMixedAir?: AirState;    // 난방 혼합공기 상태 (m1)
  heatingSupplyAir?: AirState;   // 난방 급기 상태 (l2)
  heatingAfterHeating?: AirState; // 난방 가열 후 상태 (l3)

  // ADP 계산 결과
  adp?: {
    temperature: number;      // ADP 온도 (°C)
    humidityRatio: number;    // ADP 습도비 (kg/kg)
    relativeHumidity: number; // ADP 상대습도 (% - 항상 100%)
    bypassFactor: number;     // Bypass Factor (%)
    valid: boolean;           // ADP 계산 유효성
    message: string;          // 계산 메시지
  };
  
  // 계산된 값들
  airChangeRate?: number;     // 환기횟수 (회/h)
  totalHeat?: number;         // 총 열량 (W)
  
  // 팬 계산 결과
  supplyFan?: {
    airflow: number;          // 급기 풍량 (CMH)
    staticPressure: number;   // 급기 정압 (Pa)
    power: number;            // 급기 동력 (kW)
    tempRise: number;         // 온도상승 (℃)
  };
  returnFan?: {
    airflow: number;          // 환기 풍량 (CMH)
    staticPressure: number;   // 환기 정압 (Pa)
    power: number;            // 환기 동력 (kW)
  };
  
  // SAte(급기팬온도상승) 동적계산 결과
  supplyFanTempRise?: number; // SAte (℃)

  // 외기부하 계산 결과
  outdoorCoolingLoad?: number; // 냉방외기부하 (W)
  outdoorHeatingLoad?: number; // 난방외기부하 (W)

  // 팬 부하 및 조명 부하 (냉방 Add & Fan, 난방 Add & Hum 계산용)
  supplyFanLoad?: number;     // SFte (W) - 급기팬 부하
  returnFanLoad?: number;     // RFte (W) - 환기팬 부하
  ceilingLightingLoad?: number; // Cceil (W) - 천장 조명 부하
  supplyAirLoad?: number;     // SAlo (W) - 급기 부하 (SFte for Draw type)
  returnAirLoad?: number;     // RAlo (W) - 환기 부하 (SFte + RFte + Cceil)

  valid: boolean;             // 전체 계산 유효성
  messages: string[];         // 계산 과정 메시지들
}

export class AHUCalculator {
  
  /**
   * 메인 AHU 계산 함수
   * VBA ahucode3.bas의 계산 로직을 JavaScript로 변환
   */
  static calculate(inputs: AHUInputs): AHUResults {
    const elevation = inputs.elevation || 0;
    const messages: string[] = [];
    
    try {
      // 1. 외기 상태 계산 (OA1)
      const outdoorAir = this.calculateOutdoorAir(inputs, elevation);
      
      // 2. 환기 상태 계산 (RA1)
      const returnAir = this.calculateReturnAir(inputs, elevation);
      
      // 3. 혼합공기 상태 계산 (EA1)
      const mixedAir = this.calculateMixedAir(outdoorAir, returnAir, inputs);
      
      // 4. 급기 상태 계산 (LA1)
      const supplyAir = this.calculateSupplyAir(inputs, returnAir, elevation, messages);

      // 5. ADP 계산 (부하가 있을 때만)
      let adp;
      if (inputs.sensibleHeat + inputs.latentHeat > 0 && inputs.hasCoolingCoil) {
        // 급기 RH가 100% 초과하면 ADP 계산 불가 (물리적으로 불가능)
        if (supplyAir.relativeHumidity > 100) {
          adp = {
            temperature: 0,
            humidityRatio: 0,
            bypassFactor: 0,
            valid: false,
            message: `급기 RH ${PsychrometricCalculator.vbaRound(supplyAir.relativeHumidity, 1)}% 초과. △T 낮추세요`
          };
        } else {
          adp = this.calculateADP(mixedAir, supplyAir, inputs, elevation);
        }
      }
      
      // 6. 환기횟수 계산
      const airChangeRate = this.calculateAirChangeRate(inputs);
      
      // 7. 팬 계산 결과
      const supplyFan = this.calculateSupplyFanResults(inputs, messages);
      const returnFan = this.calculateReturnFanResults(inputs, messages);
      
      // 8. SAte(급기팬온도상승) 동적 계산
      const supplyFanTempRise = this.calculateSupplyFanTempRise(inputs);
      //console.log("-----------------supplyFanTempRise: , inputs:", supplyFanTempRise, inputs);

      // 8-1. 팬 부하 및 조명 부하 계산 (냉방 Add & Fan, 난방 Add & Hum용)
      const fanLoadResults = this.calculateFanLoads(inputs);

      // 9. 난방 조건 계산 (난방 조건이 제공된 경우에만)
      let heatingResults;
      if (inputs.heatingRoomTemp !== undefined && inputs.heatingOutdoorTemp !== undefined) {
        heatingResults = this.calculateHeatingConditions(inputs, elevation);
      }

      // 10. 냉방외기부하 계산
      // VBA ahucode0.bas:640: ahdata.Cells(5, 107) = Application.RoundUp(0.334 * SAvol * (Application.Round(EA1ent, 2) - Application.Round(RA1ent, 2)), 0)
      // 공식: RoundUp(0.334 × 급기풍량 × (Round(혼합공기엔탈피, 2) - Round(환기엔탈피, 2)), 0)
      const outdoorCoolingLoad = inputs.supplyAirflow > 0
        ? PsychrometricCalculator.vbaRoundUp(
            0.334 * inputs.supplyAirflow * (
              PsychrometricCalculator.vbaRound(mixedAir.enthalpy, 2) -
              PsychrometricCalculator.vbaRound(returnAir.enthalpy, 2)
            ),
            0
          )
        : 0;
      // console.log('🔍 [냉방외기부하 계산]', {
      //   'supplyAirflow (급기풍량)': inputs.supplyAirflow,
      //   'mixedAir.enthalpy (혼합공기)': mixedAir.enthalpy,
      //   'returnAir.enthalpy (환기)': returnAir.enthalpy,
      //   'Round(mixedAir.enthalpy, 2)': PsychrometricCalculator.vbaRound(mixedAir.enthalpy, 2),
      //   'Round(returnAir.enthalpy, 2)': PsychrometricCalculator.vbaRound(returnAir.enthalpy, 2),
      //   'enthalpy diff': PsychrometricCalculator.vbaRound(mixedAir.enthalpy, 2) - PsychrometricCalculator.vbaRound(returnAir.enthalpy, 2),
      //   '계산식': `RoundUp(0.334 × ${inputs.supplyAirflow} × ${(PsychrometricCalculator.vbaRound(mixedAir.enthalpy, 2) - PsychrometricCalculator.vbaRound(returnAir.enthalpy, 2)).toFixed(4)}, 0)`,
      //   'outdoorCoolingLoad': outdoorCoolingLoad
      // });

      // 11. 난방외기부하 계산 (난방 조건이 있을 때만)
      // VBA ahucode0.bas:792: Cells(5, 108) = 0.335 * SAvol * (RAhdb - EAh1db)
      // VBA print_sarek2.bas:2139: OA Load % = Cells(Re, 108) / (예열+재열) * 100
      // 온도 차이 기반 (현열부하)
      let outdoorHeatingLoad = 0;
      if (heatingResults && heatingResults.heatingOutdoorAir && heatingResults.heatingReturnAir) {
        const tempDiff = heatingResults.heatingReturnAir.dryBulbTemp - heatingResults.heatingOutdoorAir.dryBulbTemp;
        outdoorHeatingLoad = inputs.outdoorAirflow > 0
          ? Math.round(0.335 * inputs.outdoorAirflow * tempDiff)
          : 0;
      }

      return {
        outdoorAir,
        returnAir,
        mixedAir,
        supplyAir,
        adp,
        airChangeRate,
        supplyFan,
        returnFan,
        supplyFanTempRise,
        outdoorCoolingLoad,
        outdoorHeatingLoad,
        ...fanLoadResults,
        ...heatingResults,
        valid: true,
        messages
      };
      
    } catch (_error) {
      return {
        outdoorAir: { dryBulbTemp: 0, relativeHumidity: 0, humidityRatio: 0, enthalpy: 0 },
        returnAir: { dryBulbTemp: 0, relativeHumidity: 0, humidityRatio: 0, enthalpy: 0 },
        mixedAir: { dryBulbTemp: 0, relativeHumidity: 0, humidityRatio: 0, enthalpy: 0 },
        supplyAir: { dryBulbTemp: 0, relativeHumidity: 0, humidityRatio: 0, enthalpy: 0 },
        valid: false,
        messages: [`계산 오류: ${(_error as Error).message}`]
      };
    }
  }
  
  /**
   * 외기 상태 계산 (OA1) - VBA 배열 회수기 로직 반영
   */
  private static calculateOutdoorAir(inputs: AHUInputs, elevation: number): AirState {
    // 1. 외기 적용 시각 결정 (표준 적용 또는 피크 시각 적용)
    let effectiveHour: number;
    if (inputs.outdoorTimeApplication === 'standard') {
      effectiveHour = 15; // 표준 적용: 15시 (오후 3시) 고정
    } else {
      effectiveHour = inputs.currentHour || parseInt(inputs.outdoorApplicationTime || '15'); // 피크 시각 적용: 사용자 입력 시각
    }
    
    // 2. ASHRAE 시간별 온도 계산 (표준 적용 vs 피크 시각 적용에 따라 다르게 처리)
    let actualOutdoorTemp = inputs.outdoorTemp;
    if (inputs.outdoorTimeApplication === 'peak' && inputs.peakTemperature && inputs.dailyRange) {
      // 피크 시각 적용: ASHRAE 시간별 온도 계산 사용
      actualOutdoorTemp = calculateASHRAETemperature(effectiveHour, inputs.peakTemperature, inputs.dailyRange);
    }
    // 표준 적용: 기본 외기온도 그대로 사용 (inputs.outdoorTemp)
    
    // 2. 원본 외기 상태 계산 (ASHRAE 온도 적용) - VBA Single precision 사용
    const originalOutdoorState = PsychrometricCalculator.calculateVBA(
      actualOutdoorTemp,
      inputs.outdoorHumidity,
      elevation
    );

    // 디버깅: 외기 습도비 비교
    const outdoorState_original = PsychrometricCalculator.calculate({
      dryBulbTemp: actualOutdoorTemp,
      relativeHumidity: inputs.outdoorHumidity,
      altitude: elevation
    });
    
    if (outdoorState_original) {
    }
    
    if (!originalOutdoorState) {
      throw new Error('외기 상태 계산 실패');
    }

    // 3. 배열 회수기가 없는 경우 원본 상태 그대로 반환
    if (!inputs.heatRecoveryType || inputs.heatRecoveryType === 'None') {
      return {
        dryBulbTemp: PsychrometricCalculator.vbaRound(actualOutdoorTemp, 1),
        relativeHumidity: PsychrometricCalculator.vbaRound(originalOutdoorState.relativeHumidity, 1),
        humidityRatio: PsychrometricCalculator.vbaRound(originalOutdoorState.humidityRatio, 4),  // ⚠️ 4자리로 수정!
        enthalpy: PsychrometricCalculator.vbaRound(originalOutdoorState.enthalpy, 2)
      };
    }

    // 3. 배열 회수기가 있는 경우 - 실내 조건 필요
    const returnAirState = PsychrometricCalculator.calculate({
      dryBulbTemp: inputs.roomTemp,
      relativeHumidity: inputs.roomHumidity,
      altitude: elevation
    });

    if (!returnAirState) {
      throw new Error('환기 상태 계산 실패 (배열 회수기 적용)');
    }

    // 4. VBA 배열 회수기 로직 적용
    const coolingEfficiency = (inputs.coolingRecoveryEfficiency || 70) / 100; // 기본값 70%
    
    let recoveredTemp = actualOutdoorTemp;
    let recoveredHumidityRatio = originalOutdoorState.humidityRatio;

    if (inputs.heatRecoveryType === 'S.H') {
      // 현열 회수기 - 온도만 회수
      recoveredTemp = coolingEfficiency * inputs.roomTemp + actualOutdoorTemp * (1 - coolingEfficiency);
    } else if (inputs.heatRecoveryType === 'T.H') {
      // 전열 회수기 - 온도와 습도 모두 회수
      recoveredTemp = coolingEfficiency * inputs.roomTemp + actualOutdoorTemp * (1 - coolingEfficiency);
      recoveredHumidityRatio = coolingEfficiency * returnAirState.humidityRatio + originalOutdoorState.humidityRatio * (1 - coolingEfficiency);
    }

    // 5. 회수 후 상태 계산
    const recoveredState = PsychrometricCalculator.calculate({
      dryBulbTemp: recoveredTemp,
      humidityRatio: recoveredHumidityRatio,
      altitude: elevation
    });

    if (!recoveredState) {
      throw new Error('배열 회수기 적용 후 상태 계산 실패');
    }
    
    return {
      dryBulbTemp: PsychrometricCalculator.vbaRound(recoveredState.dryBulbTemp, 1),
      relativeHumidity: PsychrometricCalculator.vbaRound(recoveredState.relativeHumidity, 1),
      humidityRatio: PsychrometricCalculator.vbaRound(recoveredState.humidityRatio, 4),  // ⚠️ 4자리로 수정!
      enthalpy: PsychrometricCalculator.vbaRound(recoveredState.enthalpy, 2)
    };
  }
  
  /**
   * RA 온도상승 계산 (RAte)
   * VBA: RAte = RAlo / (0.335 * RAvol)
   * RAlo = SFte + RFte + Cceil (팬부하들 + RA조명부하)
   */
  private static calculateReturnAirTempRise(inputs: AHUInputs): number {
    // RA Lighting Load 계산 (Cceil)
    // 환기 조명부하가 0보다 크면 자동으로 'separate' 모드 적용
    // 0이면 'no' 모드로 적용 무시
    let cceil = 0;
    const ventilationLightingLoad = inputs.ventilationLightingLoad || 0;
    if (ventilationLightingLoad > 0) {
      cceil = ventilationLightingLoad;
    }


    // 팬 부하들 (SFte, RFte)
    const sfTe = 0; // Supply Fan Temperature rise load - RA에는 영향 없음

    // RFte 계산 (Return Fan Temperature rise)
    let rfTe = 0;
    if (inputs.hasFan && inputs.returnAirflow && inputs.returnAirflow > 0) {
      const rfLoadPercentage = inputs.rfLoadPercentage !== undefined ? inputs.rfLoadPercentage : 100; // 기본값: 100%

      // Return Fan 정압 가져오기
      let rfPressure = 311; // 기본값 (Pa)
      try {
        const fanSelectionData = localStorage.getItem('fan-selection-form-data');
        if (fanSelectionData) {
          const parsedData = JSON.parse(fanSelectionData);
          const calculatedStaticPressure = parsedData.returnFan?.staticPressure;
          if (calculatedStaticPressure && calculatedStaticPressure > 0) {
            rfPressure = calculatedStaticPressure;
          }
        }
      } catch (error) {
        // 기본값 사용
      }

      // Return Fan 효율 가져오기
      let rfEfficiency = 50; // 기본값 (%)
      let rfMotorSafetyFactor = 15; // 기본값 (%)
      try {
        const fanSelectionData = localStorage.getItem('fan-selection-form-data');
        if (fanSelectionData) {
          const parsedData = JSON.parse(fanSelectionData);
          rfEfficiency = parsedData.returnFan?.efficiency || 50;
          rfMotorSafetyFactor = parsedData.returnFan?.motorSafetyFactor || 15;
        }
      } catch (error) {
        // 기본값 사용
      }

      // VBA Single 정밀도 변환 함수
      const toVBASingle = (value: number): number => {
        const buffer = new ArrayBuffer(4);
        const float32 = new Float32Array(buffer);
        float32[0] = value;
        return float32[0];
      };

      // RFte 계산 공식 (SA Fan과 동일한 방식)
      // RFte = (rfLoadPercentage/100) * returnAirflow * rfPressure / (3600 * rfEfficiency/100) * (1 + rfMotorSafetyFactor/100)
      const ratio = toVBASingle(rfLoadPercentage / 100);
      const efficiency = toVBASingle(rfEfficiency / 100);
      const safety = toVBASingle(1 + rfMotorSafetyFactor / 100);

      // Inside motor 타입으로 가정 (모터 안전율 포함)
      const RFte_power = toVBASingle(toVBASingle(toVBASingle(ratio * inputs.returnAirflow) * rfPressure) / toVBASingle(3600 * efficiency) * safety);

      // RFte = RFte_power / (0.335 * returnAirflow)
      if (RFte_power > 0 && inputs.returnAirflow > 0) {
        const constant335 = toVBASingle(0.335);
        const denominator = toVBASingle(constant335 * inputs.returnAirflow);
        const RFte1 = toVBASingle(RFte_power / denominator);

        // VBA 반올림 로직 적용
        const RFte1Str = RFte1.toString();
        const dotIndex = RFte1Str.indexOf('.');
        let rightTwo = "";
        if (RFte1Str.length >= 2) {
          rightTwo = RFte1Str.slice(-2);
        } else {
          rightTwo = RFte1Str;
        }

        if (dotIndex === -1 || rightTwo === ".") {
          rfTe = RFte1;
        } else {
          rfTe = toVBASingle(PsychrometricCalculator.vbaRoundUp(RFte1, 1));
        }
      }
    }

    // RAlo = SFte + RFte + Cceil
    const raLo = sfTe + rfTe + cceil;

    // RAte 계산
    let raTe = 0;
    if (inputs.returnAirflow && inputs.returnAirflow > 0) {
      const raTe1 = raLo / (0.335 * inputs.returnAirflow);

      // VBA 반올림 로직 구현
      const raTe1Str = raTe1.toString();
      const dotIndex = raTe1Str.indexOf('.');
      if (raTe1Str.endsWith('.') || dotIndex === -1) {
        raTe = raTe1;
      } else {
        raTe = PsychrometricCalculator.vbaRoundUp(raTe1, 1);
      }
    }

    return raTe;
  }

  /**
   * 환기 상태 계산 (RA1)
   * VBA: RA1db = RAdb + RAte (RA 온도상승 적용)
   */
  private static calculateReturnAir(inputs: AHUInputs, elevation: number): AirState {
    // VBA와 정확히 동일한 계산 순서
    const RAdb = inputs.roomTemp;
    const RArh = inputs.roomHumidity;
    
    // 1. RAabs = xfuc!(RAdb, RArh, rmbook1.Range("g11")) - VBA Single precision 사용
    const RAabs = PsychrometricCalculator.calculateHumidityRatioVBA(RAdb, RArh, elevation);
    const RAabs_original = PsychrometricCalculator.calculateHumidityRatio(RAdb, RArh, elevation);
    
    // 2. RA 온도상승 계산 (RAte)
    const RAte = this.calculateReturnAirTempRise(inputs);

    // 디버깅: RAte 값 확인
    if (inputs.supplyAirflow === 25600 && Math.abs(inputs.outdoorAirflow - 8700) < 1) {
    }

    // 3. VBA 조건문 적용: If RAte = 0 Then ... ElseIf RAte > 0 Then
    let RA1db: number;
    let RA1rh: number;
    let RA1abs: number;
    let RA1ent: number;

    if (RAte === 0) {
      // RAte = 0인 경우 원본 값 사용
      RA1db = RAdb;
      RA1rh = RArh;
      RA1abs = RAabs;
      RA1ent = PsychrometricCalculator.calculateEnthalpy(RA1db, PsychrometricCalculator.vbaRound(RA1abs, 4));
    } else {
      // RAte > 0인 경우 온도상승 적용
      RA1db = RAdb + RAte;
      RA1abs = RAabs; // 습도비는 동일 유지
      
      // RA1ent = entfuc!(RA1db, Application.Round(RA1abs, 4))
      const roundedRA1abs = PsychrometricCalculator.vbaRound(RA1abs, 4);
      RA1ent = PsychrometricCalculator.calculateEnthalpy(RA1db, roundedRA1abs);
      
      // RA1rh = rhfuc!(RA1db, RA1abs, rmbook1.Range("g11"))
      RA1rh = PsychrometricCalculator.calculateRelativeHumidity(RA1db, RA1abs, elevation);
    }

    return {
      dryBulbTemp: RA1db,      // VBA RA1db (온도상승 적용)
      relativeHumidity: RA1rh, // VBA RA1rh
      humidityRatio: RA1abs,   // VBA RA1abs
      enthalpy: RA1ent         // VBA RA1ent
    };
  }
  
  /**
   * 혼합공기 상태 계산 (EA1)
   * VBA ahucode3.bas 1328-1351의 복잡한 반올림 로직을 VBA Single 정밀도로 정확히 구현
   */
  private static calculateMixedAir(outdoorAir: AirState, returnAir: AirState, inputs: AHUInputs): AirState {
    // VBA Single 타입 정밀도 적용 함수
    const toVBASingle = (value: number): number => {
      // IEEE 754 32-bit float 정확히 모사
      const buffer = new ArrayBuffer(4);
      const float32 = new Float32Array(buffer);
      float32[0] = value;
      return float32[0];
    };

    // supplyAirflow가 0 이하일 경우 계산을 중단하고 환기 상태를 반환
    if (inputs.supplyAirflow <= 0) {
      return returnAir;
    }

    // VBA: Airr = OAvol / SAvol (Single 정밀도)
    let airRatio = toVBASingle(inputs.outdoorAirflow / inputs.supplyAirflow);

    // VBA의 특별한 경우 처리
    if (inputs.outdoorAirflow === 0) {
      // If OAvol = 0 Then EA1 = RA1
      return {
        dryBulbTemp: returnAir.dryBulbTemp,
        relativeHumidity: returnAir.relativeHumidity,
        humidityRatio: returnAir.humidityRatio,
        enthalpy: returnAir.enthalpy
      };
    } else if (inputs.outdoorAirflow === inputs.supplyAirflow) {
      // ElseIf OAvol = SAvol Then EA1 = OA1
      return {
        dryBulbTemp: outdoorAir.dryBulbTemp,
        relativeHumidity: outdoorAir.relativeHumidity,
        humidityRatio: outdoorAir.humidityRatio,
        enthalpy: outdoorAir.enthalpy
      };
    } else {
      // VBA의 정확한 반올림 조건: Right(Airr, 2) = "." Or Right(Airr, 3) = "."
      const airrStr = airRatio.toString();
      let roundedAirr: number;
      
      // VBA 조건: 문자열 끝의 2자리나 3자리가 "."인 경우
      if (airrStr.slice(-2) === '.' || airrStr.slice(-3) === '.') {
        // Application.Round(Airr, 2) 사용
        roundedAirr = toVBASingle(Math.round(airRatio * 100) / 100);
      } else {
        // Application.RoundUp(Airr, 2) 사용
        roundedAirr = toVBASingle(Math.ceil(airRatio * 100) / 100);
      }

      // Airr이 1.0을 초과하면 물리적으로 불가능 (외기량 > 급기량)
      // 1.0으로 제한하고 경고 표시
      if (roundedAirr > 1.0) {
        console.warn(`⚠️ 외기량(${inputs.outdoorAirflow})이 급기량(${inputs.supplyAirflow})을 초과합니다. Airr=${airRatio.toFixed(4)} → 1.0으로 제한`);
        roundedAirr = toVBASingle(1.0);
      }

      // VBA 반올림 정밀도를 Single 타입으로 적용
      const roundedOATemp = toVBASingle(Math.round(outdoorAir.dryBulbTemp * 10) / 10);
      const roundedRATemp = toVBASingle(Math.round(returnAir.dryBulbTemp * 10) / 10);
      const roundedOAHumidity = toVBASingle(PsychrometricCalculator.vbaRound(outdoorAir.humidityRatio, 4));
      const roundedRAHumidity = toVBASingle(PsychrometricCalculator.vbaRound(returnAir.humidityRatio, 4));

      // VBA 공식을 Single 정밀도로 구현
      const mixedTemp = toVBASingle(toVBASingle(roundedAirr * roundedOATemp) + toVBASingle((1 - roundedAirr) * roundedRATemp));
      const mixedHumidityRatio = toVBASingle(toVBASingle(roundedAirr * roundedOAHumidity) + toVBASingle((1 - roundedAirr) * roundedRAHumidity));

      // EA1ent = entfuc!(EA1db, Application.Round(EA1abs, 4)) - Single 정밀도
      const roundedMixedHumidity = toVBASingle(PsychrometricCalculator.vbaRound(mixedHumidityRatio, 4));
      const mixedEnthalpy = toVBASingle(PsychrometricCalculator.calculateEnthalpy(mixedTemp, roundedMixedHumidity));
      
      // EA1rh = rhfuc!(EA1db, EA1abs, elevation) - Single 정밀도
      const mixedRH = toVBASingle(PsychrometricCalculator.calculateRelativeHumidity(mixedTemp, mixedHumidityRatio, inputs.elevation || 0));

      // 디버깅 로그
      if (inputs.supplyAirflow === 25600 && Math.abs(inputs.outdoorAirflow - 8700) < 1) {
      }

      return {
        dryBulbTemp: toVBASingle(mixedTemp),
        relativeHumidity: toVBASingle(mixedRH),
        humidityRatio: toVBASingle(mixedHumidityRatio),
        enthalpy: toVBASingle(mixedEnthalpy)
      };
    }
  }
  
  /**
   * 급기 팬 온도상승 (SAte) 동적 계산
   * VBA ahucode3.bas의 SFte → SAlo → SAte 로직 구현
   * VBA 공식: SFte = (fanRatio/100) * SAvol * pressure / (3600 * (fanEff/100) * (1 + motorSafety/100))
   */
  private static calculateSupplyFanTempRise(inputs: AHUInputs): number {
    // VBA Single 타입 정밀도 적용 함수
    const toVBASingle = (value: number): number => {
      // IEEE 754 32-bit float 정확히 모사
      const buffer = new ArrayBuffer(4);
      const float32 = new Float32Array(buffer);
      float32[0] = value;
      return float32[0];
    };

    // 팬이 비활성화되어 있으면 SAte = 0
    if (!inputs.hasFan || inputs.supplyAirflow <= 0) {
      console.log('🔍 [SA Fan(△T)] 계산 안됨:', { hasFan: inputs.hasFan, supplyAirflow: inputs.supplyAirflow });
      return 0;
    }

    // VBA TextBoxfn1 값 - AHU 선정 화면의 SF부하 적용 비율 사용
    // inputs.sfLoadPercentage로 메모리에서 직접 전달받음
    const fanRatio = inputs.sfLoadPercentage || 100; // 기본값: 100% (고정값)

    const fanEfficiency = inputs.fanEfficiency || 60; // 사용자 입력값 우선, 기본값: 60% (VBA TextBoxf19 기본값)
    const motorSafetyFactor = inputs.motorSafetyFactor || 15; // 사용자 입력값 우선, 기본값: 15%

    // FAN 선정 탭에서 계산된 Supply Fan 정압을 우선적으로 사용
    // inputs.fanPressure는 AHUSelectionForm.tsx에서 이미 Fan Selection 탭 데이터를 읽어서 전달한 값
    const fanPressure = inputs.fanPressure;

    if (!fanPressure || fanPressure <= 0) {
      // FAN 선정 탭에서 데이터를 아직 입력하지 않은 경우 (정상적인 상황)
      // console.warn으로 변경하여 개발 콘솔을 덜 어지럽힘
      if (process.env.NODE_ENV === 'development') {
        console.debug('⚠️ [SA Fan(△T)] fanPressure 미입력 - Fan Selection 탭에서 정압 데이터 입력 필요');
      }
      return 0;
    }

    // VBA SFte 계산공식
    // SFte = Val(TextBoxfn1.Text) / 100 * SAvol * Val(TextBoxf24.Text) / (3600 * Val(TextBoxf19.Text) / 100) * (1 + Val(TextBoxf20.Text) / 100)
    let SFte = 0;
    
    if (inputs.fanType === 'Draw(I)' || inputs.fanType === 'Blow(I)') {
      // Inside motor type (모터 안전율 포함) - VBA Single 정밀도 적용
      const ratio = toVBASingle(fanRatio / 100);
      const efficiency = toVBASingle(fanEfficiency / 100);
      const safety = toVBASingle(1 + motorSafetyFactor / 100);
      SFte = toVBASingle(toVBASingle(toVBASingle(ratio * inputs.supplyAirflow) * fanPressure) / toVBASingle(3600 * efficiency) * safety);
    } else if (inputs.fanType === 'Draw(O)' || inputs.fanType === 'Blow(O)') {
      // Outside motor type (모터 안전율 제외) - VBA Single 정밀도 적용
      const ratio = toVBASingle(fanRatio / 100);
      const efficiency = toVBASingle(fanEfficiency / 100);
      SFte = toVBASingle(toVBASingle(toVBASingle(ratio * inputs.supplyAirflow) * fanPressure) / toVBASingle(3600 * efficiency));
    }

    // VBA: SAlo = SFte (Draw 타입인 경우만)
    let SAlo = 0;
    if (inputs.fanType === 'Draw(I)' || inputs.fanType === 'Draw(O)') {
      SAlo = SFte;
    }
    
    // VBA: SAte = SAlo / (0.335 * SAvol) with RoundUp
    let SAte = 0;


    if (SAlo > 0 && inputs.supplyAirflow > 0) {
      // VBA Single 정밀도 적용: SAte = SAlo / (0.335 * SAvol)
      const constant335 = toVBASingle(0.335);
      const denominator = toVBASingle(constant335 * inputs.supplyAirflow);
      const SAte1 = toVBASingle(SAlo / denominator);

      // VBA 반올림 로직: Right(SAte1, 2) = "." Or P = 0 체크
      const SAte1Str = SAte1.toString();
      const dotIndex = SAte1Str.indexOf('.');

      // VBA Right(SAte1, 2) = "." 조건 정확히 구현
      // VBA에서 Right(string, n)이 요청된 길이보다 문자열이 짧으면 전체 문자열을 반환
      let rightTwo = "";
      if (SAte1Str.length >= 2) {
        rightTwo = SAte1Str.slice(-2);
      } else {
        rightTwo = SAte1Str;
      }

      // P = 0 (소수점이 없음) 또는 Right(SAte1, 2) = "." 조건
      if (dotIndex === -1 || rightTwo === ".") {
        SAte = SAte1;
      } else {
        SAte = toVBASingle(PsychrometricCalculator.vbaRoundUp(SAte1, 1));
      }

    }

    return SAte;
  }

  /**
   * 팬 부하 및 조명 부하 계산 (냉방 Add & Fan, 난방 Add & Hum용)
   * VBA: Cells(5, 109) = RoundUp(RAlo + SAlo - Cceil, 0)
   * RAlo = SFte + RFte + Cceil
   * SAlo = SFte (Draw 타입) or 0 (Blow 타입)
   * Cceil = 환기조명부하 (RA Lighting Load)
   */
  private static calculateFanLoads(inputs: AHUInputs): {
    supplyFanLoad?: number;
    returnFanLoad?: number;
    ceilingLightingLoad?: number;
    supplyAirLoad?: number;
    returnAirLoad?: number;
  } {
    // VBA Single 타입 정밀도 적용 함수
    const toVBASingle = (value: number): number => {
      const buffer = new ArrayBuffer(4);
      const float32 = new Float32Array(buffer);
      float32[0] = value;
      return float32[0];
    };

    // 1. Cceil 계산 (RA Lighting Load - 천장 조명 부하)
    const Cceil = inputs.ventilationLightingLoad || 0;

    // 2. SFte 계산 (Supply Fan Temperature rise load)
    let SFte = 0;
    if (inputs.hasFan && inputs.supplyAirflow > 0) {
      const fanRatio = inputs.sfLoadPercentage || 100;
      const fanEfficiency = inputs.fanEfficiency || 60;
      const motorSafetyFactor = inputs.motorSafetyFactor || 15;

      let fanPressure = inputs.fanPressure || 750;
      try {
        const fanSelectionData = localStorage.getItem('fan-selection-form-data');
        if (fanSelectionData) {
          const parsedData = JSON.parse(fanSelectionData);
          const calculatedStaticPressure = parsedData.supplyFan?.staticPressure;
          if (calculatedStaticPressure && calculatedStaticPressure > 0) {
            fanPressure = calculatedStaticPressure;
          }
        }
      } catch (error) {
        // 기본값 사용
      }

      if (inputs.fanType === 'Draw(I)' || inputs.fanType === 'Blow(I)') {
        const ratio = toVBASingle(fanRatio / 100);
        const efficiency = toVBASingle(fanEfficiency / 100);
        const safety = toVBASingle(1 + motorSafetyFactor / 100);
        SFte = toVBASingle(toVBASingle(toVBASingle(ratio * inputs.supplyAirflow) * fanPressure) / toVBASingle(3600 * efficiency) * safety);
      } else if (inputs.fanType === 'Draw(O)' || inputs.fanType === 'Blow(O)') {
        const ratio = toVBASingle(fanRatio / 100);
        const efficiency = toVBASingle(fanEfficiency / 100);
        SFte = toVBASingle(toVBASingle(toVBASingle(ratio * inputs.supplyAirflow) * fanPressure) / toVBASingle(3600 * efficiency));
      }
    }

    // 3. RFte 계산 (Return Fan Temperature rise load)
    let RFte = 0;
    if (inputs.hasFan && inputs.returnAirflow && inputs.returnAirflow > 0) {
      const rfLoadPercentage = inputs.rfLoadPercentage !== undefined ? inputs.rfLoadPercentage : 100;

      let rfPressure = 311;
      let rfEfficiency = 50;
      let rfMotorSafetyFactor = 15;
      try {
        const fanSelectionData = localStorage.getItem('fan-selection-form-data');
        if (fanSelectionData) {
          const parsedData = JSON.parse(fanSelectionData);
          if (parsedData.returnFan?.staticPressure) {
            rfPressure = parsedData.returnFan.staticPressure;
          }
          rfEfficiency = parsedData.returnFan?.efficiency || 50;
          rfMotorSafetyFactor = parsedData.returnFan?.motorSafetyFactor || 15;
        }
      } catch (error) {
        // 기본값 사용
      }

      const ratio = toVBASingle(rfLoadPercentage / 100);
      const efficiency = toVBASingle(rfEfficiency / 100);
      const safety = toVBASingle(1 + rfMotorSafetyFactor / 100);
      const RFte_power = toVBASingle(toVBASingle(toVBASingle(ratio * inputs.returnAirflow) * rfPressure) / toVBASingle(3600 * efficiency) * safety);

      if (RFte_power > 0 && inputs.returnAirflow > 0) {
        const constant335 = toVBASingle(0.335);
        const denominator = toVBASingle(constant335 * inputs.returnAirflow);
        const RFte1 = toVBASingle(RFte_power / denominator);

        const RFte1Str = RFte1.toString();
        const dotIndex = RFte1Str.indexOf('.');
        let rightTwo = RFte1Str.length >= 2 ? RFte1Str.slice(-2) : RFte1Str;

        if (dotIndex === -1 || rightTwo === ".") {
          RFte = RFte1;
        } else {
          RFte = toVBASingle(PsychrometricCalculator.vbaRoundUp(RFte1, 1));
        }
      }
    }

    // 4. SAlo 계산 (Supply Air Load)
    let SAlo = 0;
    if (inputs.fanType === 'Draw(I)' || inputs.fanType === 'Draw(O)') {
      SAlo = SFte;
    }

    // 5. RAlo 계산 (Return Air Load)
    const RAlo = SFte + RFte + Cceil;

    return {
      supplyFanLoad: SFte,
      returnFanLoad: RFte,
      ceilingLightingLoad: Cceil,
      supplyAirLoad: SAlo,
      returnAirLoad: RAlo
    };
  }

  /**
   * VBA LAh1db 계산 (냉각코일 후 온도, Leave Air Temperature)
   * VBA: LAh1db = Hla + RAhdb, 여기서 Hla = Hth / (0.335 * SAvol)
   */
  static calculateLATemperature(totalHeat: number, supplyAirVolume: number, roomTemp: number): number {
    if (totalHeat === 0) {
      return roomTemp;
    } else if (totalHeat > 0) {
      const hla = totalHeat / (0.335 * supplyAirVolume);
      // VBA 반올림 로직: Right(Hla, 2) = "." Or P = 0 체크
      const hlaStr = hla.toString();
      const dotIndex = hlaStr.indexOf('.');

      let finalHla: number;
      if (hlaStr.slice(-2) === '.' || dotIndex === -1) {
        finalHla = hla;
      } else {
        finalHla = Math.ceil(hla * 10) / 10; // VBA Application.RoundUp(Hla, 1)
      }

      return finalHla + roomTemp;
    }
    return roomTemp;
  }

  /**
   * 급기 상태 계산 (LA1)
   * VBA의 LA1db = RAdb - CCdb - SAte - RHte 로직 구현
   */
  private static calculateSupplyAir(inputs: AHUInputs, returnAir: AirState, elevation: number, messages: string[]): AirState {
    let supplyTemp: number;
    let supplyRH: number;
    let supplyHumidityRatio: number;
    
    // SAte 계산: 동적 계산 우선, 없으면 기존 saFanTemp 사용
    const dynamicSAte = this.calculateSupplyFanTempRise(inputs);
    const actualSAte = dynamicSAte > 0 ? dynamicSAte : inputs.saFanTemp;
    
    messages.push(`SAte 계산: 동적계산=${dynamicSAte.toFixed(2)}℃, 기본값=${inputs.saFanTemp}℃ → 실제사용=${actualSAte.toFixed(2)}℃`);
    
    
    // CCdb: VBA에서는 TextBoxv5.Text에서 직접 입력받은 값을 사용
    // VBA: CCdb = .TextBoxv5.Text (직접 입력값)
    const actualCCdb = inputs.coolingCoilTemp; // VBA TextBoxv5.Text에 해당
    messages.push(`CCdb (VBA TextBoxv5): ${actualCCdb}℃ (직접 입력값)`);
    
    // 참고: 계산된 CCdb 값도 표시 (디버깅용)
    if (inputs.sensibleHeat > 0 && inputs.supplyAirflow > 0) {
      const calculatedCCdb = inputs.sensibleHeat / (0.335 * inputs.supplyAirflow);
      const calculatedCCdbRounded = PsychrometricCalculator.vbaRoundUp(calculatedCCdb, 1);
      messages.push(`참고 - 계산된 CCdb: ${inputs.sensibleHeat}W / (0.335 × ${inputs.supplyAirflow}) = ${calculatedCCdbRounded.toFixed(1)}℃`);
    }
    
    const totalLoad = inputs.sensibleHeat + inputs.latentHeat;
    
    
    if (totalLoad === 0) {
      // 부하가 없는 경우 - 직접 입력값 사용
      supplyTemp = actualCCdb; // 이 경우 설정온도로 사용
      supplyRH = inputs.roomHumidity; // 기본값으로 실내습도 사용
      
      const state = PsychrometricCalculator.calculate({
        dryBulbTemp: supplyTemp,
        relativeHumidity: supplyRH,
        altitude: elevation
      });
      
      if (!state) {
        throw new Error('급기 상태 계산 실패');
      }
      
      supplyHumidityRatio = state.humidityRatio;
      messages.push(`부하 없음: 급기온도 ${supplyTemp}°C DB, ${supplyRH}% RH`);
      
    } else {
      // 부하가 있는 경우
      // VBA 정확한 공식: LA1db = RAdb - CCdb - SAte - RHte (RAdb는 원본 실내온도 사용, RA1db 아님)
      
      const calculatedSupplyTemp = inputs.roomTemp - actualCCdb - actualSAte - inputs.rhReheatTemp;
      
      // 원래 방식: 계산된 급기온도 사용 (VBA와 동일)
      supplyTemp = calculatedSupplyTemp;
      
      // 급기 후 온도 (팬 온도상승과 재열 적용)
      const supplyAfterTemp = supplyTemp + actualSAte + inputs.rhReheatTemp;
      
      // Load Line에서 습도비 계산 (VBA Lxfuc 로직)
      
      // 1. RSHF 계산 (VBA Single 정밀도 맞춤)
      const RSHF = parseFloat((inputs.sensibleHeat / (inputs.sensibleHeat + inputs.latentHeat)).toFixed(7));
      
      // 2. VBA에서는 원래 실내조건(RAdb, RArh)의 습도비/엔탈피를 Lxfuc에 전달
      const originalRoomHumidityRatio = PsychrometricCalculator.calculateHumidityRatio(inputs.roomTemp, inputs.roomHumidity, elevation);
      const originalRoomEnthalpy = PsychrometricCalculator.calculateEnthalpy(inputs.roomTemp, PsychrometricCalculator.vbaRound(originalRoomHumidityRatio, 4));
      
      // 3. VBA Single 정밀도 적용
      const vbaRoomHumidityRatio = parseFloat(originalRoomHumidityRatio.toFixed(6));
      const vbaRoomEnthalpy = parseFloat(originalRoomEnthalpy.toFixed(2));
      const vbaRSHF = parseFloat(RSHF.toFixed(7));
      const vbaSupplyAfterTemp = parseFloat(supplyAfterTemp.toFixed(2));

      // 4. Load Line에서 습도비 계산 (VBA Lxfuc)
      const rawSupplyHumidityRatio = this.calculateLoadLineHumidityRatio(
        vbaRoomHumidityRatio,
        vbaRoomEnthalpy,
        vbaRSHF,
        vbaSupplyAfterTemp
      );
      
      // VBA에서 LA1abs는 중간 계산에서는 원시값 사용
      supplyHumidityRatio = rawSupplyHumidityRatio;
      
      
      // 4. LA1rh = rhfuc!(LA1db, LA1abs, rmbook1.Range("g11"))
      // VBA rhfuc 공식 정확히 구현: rhfuc = prefuc(Z) * x / (pws * (0.621945 + x)) * 100
      
      // VBA pwsfuc 공식 (ASHRAE) - 정확한 구현
      const tKelvin = 273.15 + supplyTemp;
      let vbaPws: number;
      if (supplyTemp > 0) {
        vbaPws = (Math.exp(-5800.2206 / tKelvin + 1.3914993 - 0.048640239 * tKelvin + 0.000041764768 * 
                 Math.pow(tKelvin, 2) - 0.000000014452093 * Math.pow(tKelvin, 3) + 6.5459673 * Math.log(tKelvin))) / 1000;
      } else {
        vbaPws = (Math.exp(-5674.5359 / tKelvin + 6.3925247 - 0.009677843 * tKelvin + 0.00000062215701 *
                 Math.pow(tKelvin, 2) + 2.0747825e-09 * Math.pow(tKelvin, 3) - 9.484024e-13 * Math.pow(tKelvin, 4) + 4.1635019 *
                 Math.log(tKelvin))) / 1000;
      }
      
      // VBA prefuc 공식 사용 (elevation을 미터 단위로 가정)
      const vbaPressure = 101.325 * Math.pow(1 - 2.25577e-5 * elevation, 5.2559); // kPa
      
      // VBA 정확한 rhfuc 계산 (단위: kPa)
      const calculatedSupplyRH = vbaPressure * supplyHumidityRatio / (vbaPws * (0.621945 + supplyHumidityRatio)) * 100;

      // 상대습도 그대로 사용 (100% 초과 시 비정상 상태임을 사용자에게 표시)
      supplyRH = calculatedSupplyRH;


      // VBA 유효성 검사: LA1db < RAdb And CCdb < 25 And LA1rh < 100 And ... And LA1rh >= 85 And LA1rh < 100
      const isValidSupply = supplyTemp < inputs.roomTemp &&
                           inputs.coolingCoilTemp < 25 &&
                           supplyRH >= 85 &&
                           supplyRH < 100;

      if (isValidSupply) {
        messages.push(`급기상태: ${PsychrometricCalculator.vbaRound(supplyTemp, 1)}°C DB, ${PsychrometricCalculator.vbaRound(supplyRH, 1)}% RH`);
      } else {
        if (supplyRH >= 100) {
          messages.push(`⚠️ 급기 RH가 ${PsychrometricCalculator.vbaRound(supplyRH, 1)}%로 100% 이상 (물리적 불가능)`);
        } else if (supplyRH < 85) {
          messages.push(`급기 RH가 ${PsychrometricCalculator.vbaRound(supplyRH, 1)}%임, 85~99% 권장`);
        } else {
          messages.push(`급기 온도나 조건이 부적절함: 온도 ${PsychrometricCalculator.vbaRound(supplyTemp, 1)}°C, RH ${PsychrometricCalculator.vbaRound(supplyRH, 1)}%`);
        }
      }
    }
    
    const supplyEnthalpy = PsychrometricCalculator.calculateEnthalpy(supplyTemp, supplyHumidityRatio);

    // VBA 최종 표시: Application.Round(LA1db, 1) & "℃ DB, " & Application.Round(LA1rh, 1) & "% RH"
    return {
      dryBulbTemp: PsychrometricCalculator.vbaRound(supplyTemp, 1),
      relativeHumidity: PsychrometricCalculator.vbaRound(supplyRH, 1),
      humidityRatio: PsychrometricCalculator.vbaRound(supplyHumidityRatio, 5),
      enthalpy: PsychrometricCalculator.vbaRound(supplyEnthalpy, 2)
    };
  }
  
  /**
   * ADP (Apparatus Dew Point) 및 Bypass Factor 계산
   * 개선된 수치해석적 접근 방법 사용
   *
   * ADP는 냉각코일의 포화면 온도로, 혼합공기가 냉각코일을 통과할 때
   * 이론적으로 도달할 수 있는 최저 온도이며 상대습도 100%인 점입니다.
   *
   * 물리적 의미:
   * - Load Line(부하선): 실내조건에서 급기조건으로 향하는 직선
   * - Coil Line(코일선): 혼합공기에서 ADP로 향하는 직선
   * - 두 선의 교점이 실제 급기상태가 됨
   */
  private static calculateADP(mixedAir: AirState, supplyAir: AirState, inputs: AHUInputs, elevation: number) {
    let adpTemp = 0;
    let adpHumidityRatio = 0;
    let bypassFactor = 0;
    let valid = false;
    let message = '';

    // JavaScript double 정밀도 사용 (뉴튼-랩슨법에 유리)
    const ea1db = mixedAir.dryBulbTemp;
    const ea1abs = mixedAir.humidityRatio;
    const la1db = supplyAir.dryBulbTemp;
    const la1abs = supplyAir.humidityRatio;

    try {
      // 입력값 유효성 검사
      if (!isFinite(ea1db) || !isFinite(ea1abs) || !isFinite(la1db) || !isFinite(la1abs)) {
        throw new Error('유효하지 않은 입력값');
      }

      if (Math.abs(ea1db - la1db) < 0.001) {
        throw new Error('온도 차이가 너무 작아 계산 불가');
      }

      // VBA 호환 ADP 계산: 뉴튼-랩슨법 우선, 실패 시 VBA 원본 방식
      const newtonResult = this.findADPByNewtonRaphson(ea1db, ea1abs, la1db, la1abs, elevation);

      if (!newtonResult.found) {
        // 뉴튼-랩슨 실패 시 VBA 원본 방식 사용
        // console.log('뉴튼-랩슨법 실패, VBA 원본 방식으로 전환');
        const vbaResult = this.findADPByVBAMethod(ea1db, ea1abs, la1db, la1abs, elevation);
        if (vbaResult.found) {
          adpTemp = vbaResult.temperature;
          adpHumidityRatio = vbaResult.humidityRatio;
          // VBA 방법도 포화선 위의 점이므로 100% RH
        } else {
          // 급기 상태의 상대습도를 확인하여 적절한 메시지 표시
          // 상대습도가 100% 초과면 △T를 낮추고, 미만이면 높여야 함
          const supplyRH = supplyAir.relativeHumidity || 0;
          const suggestion = supplyRH > 100
            ? '△T 낮추세요'
            : '△T 높이세요';

          return {
            temperature: 0,
            humidityRatio: 0,
            bypassFactor: 0,
            valid: false,
            message: `ADP 계산 실패 (급기 RH ${PsychrometricCalculator.vbaRound(supplyRH, 1)}%). ${suggestion}`
          };
        }
      } else {
        adpTemp = newtonResult.temperature;
        adpHumidityRatio = newtonResult.humidityRatio;
      }

      // Bypass Factor 계산 (고정밀도)
      const numerator = la1db - adpTemp;
      const denominator = ea1db - adpTemp;

      if (Math.abs(denominator) < 0.001) {
        throw new Error('Bypass Factor 계산에서 분모가 0에 가까움');
      }

      bypassFactor = numerator / denominator;

      // 유효성 검사 (VBA 원본 ahucode3.bas:1440-1441)
      // VBA: ADPdb < TextBoxv1 And ((DDCC="Water" And ADPdb > (TextBox10+TextBox11)/2) Or (DDCC="DX" And ADPdb > 5)) And ADPrh <= 100
      const adpRH = 100; // ADP는 포화선 위의 점이므로 RH = 100%

      let minCoilTemp = 5; // DX 코일 기본값
      if (inputs.coolingCoilType === 'Water' && inputs.coolingInletTemp && inputs.coolingOutletTemp) {
        // Water 코일: 입구/출구 온도 평균 (VBA TextBox10, TextBox11)
        minCoilTemp = (inputs.coolingInletTemp + inputs.coolingOutletTemp) / 2;
      }

      if (adpTemp < inputs.roomTemp &&
          adpTemp > minCoilTemp &&
          adpRH <= 100 &&
          bypassFactor >= 0 && bypassFactor <= 1) {
        valid = true;
        const roundedADPdb = PsychrometricCalculator.vbaRound(adpTemp, 1);
        const bfPercent = bypassFactor * 100;
        const roundedBF = PsychrometricCalculator.vbaRound(bfPercent, 1);
        message = `${roundedADPdb}°C DB / ${roundedBF}%`;
      } else {
        valid = false;
        const roundedADPdb = PsychrometricCalculator.vbaRound(adpTemp, 1);
        const roundedMinCoil = PsychrometricCalculator.vbaRound(minCoilTemp, 1);

        // 문제 진단 및 해결 방법 제시
        let suggestion = '';
        if (adpTemp <= minCoilTemp) {
          // ADP가 코일 최저 온도보다 낮음 - 급기 온도차(△T)를 줄여야 함
          const userDeltaT = inputs.supplyTempDelta || inputs.coolingCoilTemp;
          suggestion = `급기 온도차(△T) ${PsychrometricCalculator.vbaRound(userDeltaT, 1)}°C를 줄이세요 (ADP ${roundedADPdb}°C < 최소 코일온도 ${roundedMinCoil}°C)`;
        } else if (adpTemp >= inputs.roomTemp) {
          // ADP가 실내온도보다 높음 - 급기 온도차(△T)를 늘려야 함
          const userDeltaT = inputs.supplyTempDelta || inputs.coolingCoilTemp;
          suggestion = `급기 온도차(△T) ${PsychrometricCalculator.vbaRound(userDeltaT, 1)}°C를 늘리세요 (ADP ${roundedADPdb}°C ≥ 실내온도 ${inputs.roomTemp}°C)`;
        } else if (bypassFactor < 0 || bypassFactor > 1) {
          // Bypass Factor 범위 초과
          suggestion = `부하 조건을 확인하세요 (BF ${PsychrometricCalculator.vbaRound(bypassFactor * 100, 1)}% 범위 초과)`;
        } else if (adpRH > 100) {
          // 상대습도 초과 (이론적으로 불가능하지만 계산 오류 가능)
          suggestion = `계산 오류: 포화선 초과 (RH ${PsychrometricCalculator.vbaRound(adpRH, 1)}%)`;
        }

        message = suggestion || `ADP가 ${roundedADPdb}°C로 범위를 벗어남`;
      }

    } catch (error) {
      valid = false;
      message = `ADP 계산 오류: ${(error as Error).message}`;
    }

    const finalBfPercent = bypassFactor * 100;

    return {
      temperature: PsychrometricCalculator.vbaRound(adpTemp, 1),
      humidityRatio: PsychrometricCalculator.vbaRound(adpHumidityRatio, 5),
      relativeHumidity: 100, // ADP는 항상 포화선(100% RH) 위의 점
      bypassFactor: PsychrometricCalculator.vbaRound(finalBfPercent, 1),
      valid,
      message
    };
  }

  /**
   * 올바른 ADP 계산: 다중 초기값을 시도하는 로버스트 뉴튼-랩슨법
   *
   * 이미지 분석 결과:
   * - ADP는 포화선 위의 점이며, 혼합공기(ma)에서 ADP로, ADP에서 급기(la)로 가는 과정
   * - Bypass Factor: BF = (la_temp - adp_temp) / (ma_temp - adp_temp)
   * - 코일 효과: (1-BF) = 냉각되는 공기의 비율
   *
   * 다중 초기값 시도로 수렴 안정성 확보
   */
  private static findADPByNewtonRaphson(ea1db: number, ea1abs: number, la1db: number, la1abs: number, elevation: number) {
    // 여러 초기값으로 시도하여 가장 좋은 결과 선택
    const tempDiff = ea1db - la1db;

    const initialGuesses = [
      la1db - 2,                    // 가까운 추정
      la1db - tempDiff * 0.5,      // 중간 추정
      la1db - tempDiff * 0.8,      // 멀리 추정
      la1db - 12,                  // 경험적 고정값
      la1db - Math.min(tempDiff * 0.6, 20) // 적응적 추정
    ];

    let bestResult = { found: false, temperature: 0, humidityRatio: 0, iterations: Infinity, error: Infinity };

    for (let guessIndex = 0; guessIndex < initialGuesses.length; guessIndex++) {
      const initialTemp = initialGuesses[guessIndex];
      const result = this.newtonRaphsonSingleTry(ea1db, ea1abs, la1db, la1abs, elevation, initialTemp, guessIndex);

      if (result.found && result.iterations < bestResult.iterations) {
        bestResult = result;
        // console.log(`새로운 최적 결과 발견: 초기값=${initialTemp.toFixed(1)}℃, ${result.iterations}회 반복`);
      }
    }

    if (bestResult.found) {
      // console.log(`최종 선택: ADP=${bestResult.temperature.toFixed(3)}℃, ${bestResult.iterations}회 반복`);
      return bestResult;
    }

    // console.log('모든 초기값 실패');
    return { found: false, temperature: 0, humidityRatio: 0 };
  }

  /**
   * 단일 초기값으로 뉴튼-랩슨법 시도
   */
  private static newtonRaphsonSingleTry(ea1db: number, ea1abs: number, la1db: number, la1abs: number, elevation: number, initialTemp: number, guessIndex: number) {
    const tolerance = 1e-5;
    const maxIterations = 20;

    let adpTemp = initialTemp;
    const coilSlope = (ea1abs - la1abs) / (ea1db - la1db);

    //console.log(`[시도 ${guessIndex + 1}] 초기값: ${initialTemp.toFixed(2)}℃`);

    for (let i = 0; i < maxIterations; i++) {
      // ADP 온도에서의 포화 습도비
      const adpSatHumidity = PsychrometricCalculator.calculateHumidityRatio(adpTemp, 100, elevation);

      // 코일선상에서 ADP 온도에 해당하는 습도비 계산
      const coilLineHumidity = la1abs + coilSlope * (adpTemp - la1db);

      // 목표 함수: f(T) = 코일선상의 습도비 - 포화선상의 습도비
      const f = coilLineHumidity - adpSatHumidity;

      // 수렴 확인
      if (Math.abs(f) < tolerance) {
        //console.log(`[시도 ${guessIndex + 1}] 수렴: ${i+1}회, ADP=${adpTemp.toFixed(3)}℃`);
        // ADP는 포화선 위의 점이므로 RH = 100% 정확히
        return {
          found: true,
          temperature: adpTemp,
          humidityRatio: adpSatHumidity, // 포화 습도비 사용
          relativeHumidity: 100,         // ADP는 항상 100% RH
          iterations: i + 1,
          error: Math.abs(f)
        };
      }

      // 미분값 계산 (중앙차분법)
      const deltaT = 0.001;

      const tempPlus = adpTemp + deltaT;
      const satHumidityPlus = PsychrometricCalculator.calculateHumidityRatio(tempPlus, 100, elevation);
      const coilLineHumidityPlus = la1abs + coilSlope * (tempPlus - la1db);
      const fPlus = coilLineHumidityPlus - satHumidityPlus;

      const tempMinus = adpTemp - deltaT;
      const satHumidityMinus = PsychrometricCalculator.calculateHumidityRatio(tempMinus, 100, elevation);
      const coilLineHumidityMinus = la1abs + coilSlope * (tempMinus - la1db);
      const fMinus = coilLineHumidityMinus - satHumidityMinus;

      const fPrime = (fPlus - fMinus) / (2 * deltaT);

      if (Math.abs(fPrime) < 1e-10) {
        //console.log(`[시도 ${guessIndex + 1}] 미분값 0, 실패`);
        break;
      }

      // 뉴튼-랩슨 업데이트
      const newTemp = adpTemp - f / fPrime;

      // 물리적 제약 적용
      const minTemp = la1db - 35;
      const maxTemp = la1db - 0.5;

      // 발산 방지를 위한 변화량 제한
      const maxChange = 2; // 보수적으로 설정
      let clampedTemp = Math.max(minTemp, Math.min(maxTemp, newTemp));

      if (Math.abs(clampedTemp - adpTemp) > maxChange) {
        clampedTemp = adpTemp + Math.sign(clampedTemp - adpTemp) * maxChange;
      }

      if (i % 5 === 0) { // 5회마다 로그
        //console.log(`[시도 ${guessIndex + 1}] ${i+1}회: ${adpTemp.toFixed(3)} → ${clampedTemp.toFixed(3)}℃, f=${f.toFixed(6)}`);
      }

      adpTemp = clampedTemp;
    }

    //console.log(`[시도 ${guessIndex + 1}] 최대 반복 도달, 실패`);
    return { found: false, temperature: 0, humidityRatio: 0, iterations: maxIterations, error: Infinity };
  }

  /**
   * 이분법 백업 방법 (뉴튼-랩슨법 실패 시 사용)
   */
  private static findADPByBisectionFallback(ea1db: number, ea1abs: number, la1db: number, la1abs: number, elevation: number, initialGuess?: number) {
    const tolerance = 1e-4;
    const maxIterations = 50;

    const slope = (ea1abs - la1abs) / (ea1db - la1db);

    // 탐색 범위 설정
    let lowTemp = la1db - 35;
    let highTemp = la1db - 1;

    // 초기 추정값이 있으면 범위를 좁힘
    if (initialGuess !== undefined) {
      const range = 5; // ±5℃ 범위
      lowTemp = Math.max(lowTemp, initialGuess - range);
      highTemp = Math.min(highTemp, initialGuess + range);
    }

    for (let i = 0; i < maxIterations; i++) {
      const midTemp = (lowTemp + highTemp) / 2;

      const lineHumidityRatio = la1abs + slope * (midTemp - la1db);
      const saturationHumidityRatio = PsychrometricCalculator.calculateHumidityRatio(midTemp, 100, elevation);
      const diff = lineHumidityRatio - saturationHumidityRatio;

      if (Math.abs(diff) < tolerance) {
        return {
          found: true,
          temperature: midTemp,
          humidityRatio: lineHumidityRatio
        };
      }

      if (diff > 0) {
        highTemp = midTemp;
      } else {
        lowTemp = midTemp;
      }
    }

    return { found: false, temperature: 0, humidityRatio: 0 };
  }

  /**
   * VBA 원본 방식 ADP 탐색 (정확한 호환성을 위해)
   * 1단계: 1℃씩 30회, 2단계: 0.05℃씩 20회
   */
  private static findADPByVBAMethod(ea1db: number, ea1abs: number, la1db: number, la1abs: number, elevation: number) {
    const toVBASingle = (value: number): number => {
      const buffer = new ArrayBuffer(4);
      const float32 = new Float32Array(buffer);
      float32[0] = value;
      return float32[0];
    };

    let adpTemp1 = 0;
    let adpHumidityRatio = 0;
    let adpRH = 0;

    // 1단계: 초기 ADP 온도 찾기 - VBA와 정확히 동일 (30회 반복)
    for (let j = 1; j <= 30; j++) {
      adpTemp1 = toVBASingle(la1db - j);

      // VBA: ADPabs = LA1abs - ((EA1abs - LA1abs) / (EA1db - LA1db)) * j
      const tempDiff = toVBASingle(ea1db - la1db);
      const humidityDiff = toVBASingle(ea1abs - la1abs);
      const slope = toVBASingle(humidityDiff / tempDiff);

      adpHumidityRatio = toVBASingle(la1abs - toVBASingle(slope * j));

      // VBA: ADPrh = rhfuc!(ADPdb1, ADPabs, rmbook1.Range("g11"))
      adpRH = toVBASingle(PsychrometricCalculator.calculateRelativeHumidity(adpTemp1, adpHumidityRatio, elevation));

      if (adpRH > 100) {
        break;
      }
    }

    if (adpRH < 0) {
      return { found: false, temperature: 0, humidityRatio: 0 };
    }

    // 2단계: 정밀 ADP 온도 찾기 - VBA와 정확히 동일 (20회 반복)
    let finalTemp = adpTemp1;
    let finalHumidityRatio = adpHumidityRatio;

    for (let j = 1; j <= 20; j++) {
      // VBA: ADPdb = ADPdb1 + j / 20
      const increment = toVBASingle(j / 20);
      const currentTemp = toVBASingle(adpTemp1 + increment);

      // VBA: ADPabs = LA1abs - (EA1abs - LA1abs) / (EA1db - LA1db) * (LA1db - ADPdb)
      const tempDiff = toVBASingle(ea1db - la1db);
      const humidityDiff = toVBASingle(ea1abs - la1abs);
      const slope = toVBASingle(humidityDiff / tempDiff);
      const tempFromSupply = toVBASingle(la1db - currentTemp);

      const currentHumidityRatio = toVBASingle(la1abs - toVBASingle(slope * tempFromSupply));

      // VBA: ADPrh = rhfuc!(ADPdb, ADPabs, rmbook1.Range("g11"))
      const currentRH = toVBASingle(PsychrometricCalculator.calculateRelativeHumidity(currentTemp, currentHumidityRatio, elevation));

      // VBA: If ADPrh <= 100 Then Exit For
      if (currentRH <= 100) {
        finalTemp = currentTemp;
        finalHumidityRatio = currentHumidityRatio;
        break;
      }
    }

    return {
      found: true,
      temperature: finalTemp,
      humidityRatio: finalHumidityRatio,
      relativeHumidity: 100  // ADP는 항상 100% RH
    };
  }

  /**
   * 포화선 근사 방법으로 ADP 탐색
   * 최후의 수단으로 사용하는 대안적 방법
   */
  private static findADPBySaturationApproach(ea1db: number, ea1abs: number, la1db: number, la1abs: number, elevation: number) {
    const slope = (ea1abs - la1abs) / (ea1db - la1db);

    // 온도 범위에서 포화선과 가장 가까운 점 찾기
    let bestTemp = 0;
    let bestHumidityRatio = 0;
    let minDiff = Infinity;

    for (let temp = la1db - 30; temp < la1db; temp += 0.1) {
      const calculatedHumidityRatio = la1abs + slope * (temp - la1db);
      const saturationHumidityRatio = PsychrometricCalculator.calculateHumidityRatio(temp, 100, elevation);

      const diff = Math.abs(calculatedHumidityRatio - saturationHumidityRatio);

      if (diff < minDiff) {
        minDiff = diff;
        bestTemp = temp;
        bestHumidityRatio = calculatedHumidityRatio;
      }
    }

    return {
      found: minDiff < 0.001,
      temperature: bestTemp,
      humidityRatio: bestHumidityRatio
    };
  }
  
 /**
 * Load Line 습도비 계산 (VBA Lxfuc 함수)
 * @param ex - 초과 습도비 (kg/kg)
 * @param Eent - 진입 공기 엔탈피 (kJ/kg)
 * @param SHF - 현열 비율 (0 ~ 1)
 * @param tdb - 건구온도 (°C)
 * @returns 습도비 (kg/kg)
 * @throws 입력값이 유효하지 않거나 분모가 0에 가까운 경우 오류
 */
  private static calculateLoadLineHumidityRatio(ex: number, Eent: number, SHF: number, tdb: number): number {
      // 입력값 유효성 검사
      if (isNaN(ex) || isNaN(Eent) || isNaN(SHF) || isNaN(tdb)) {
          throw new Error("유효하지 않은 입력: 모든 매개변수는 유효한 숫자여야 합니다.");
      }
      if (SHF < 0 || SHF > 1) {
          throw new Error("유효하지 않은 SHF: 0에서 1 사이의 값이어야 합니다.");
      }
      if (tdb < -50 || tdb > 50) {
          throw new Error("유효하지 않은 tdb: 온도가 비현실적인 범위(-50°C ~ 50°C)를 벗어났습니다.");
      }

      const oneMinusSHF = 1 - SHF;
      const numerator = (oneMinusSHF * Eent / 2501) - (oneMinusSHF * 1.006 * tdb / 2501) - ex;
      const denominator = (oneMinusSHF * (2501 + 1.86 * tdb) / 2501) - 1;

      if (Math.abs(denominator) < 1e-10) {
          throw new Error("0 또는 0에 가까운 분모로 인해 나눗셈 오류가 발생했습니다.");
      }

      return numerator / denominator;
  }


  
  /**
   * 환기횟수 계산
   * VBA에서 Application.RoundUp(..., 1) 사용 - 소수점 첫째 자리에서 올림
   */
  private static calculateAirChangeRate(inputs: AHUInputs): number {
    // 실제 공간 체적을 사용한 환기횟수 계산
    const area = inputs.conditionedArea || 100; // m² (기본값)
    const height = inputs.ceilingHeight || 2.7; // m (기본값)
    const roomVolume = area * height; // m³
    
    // VBA와 동일하게 Application.RoundUp(..., 1) 사용 (올림)
    return PsychrometricCalculator.vbaRoundUp(inputs.supplyAirflow / roomVolume, 1);
  }

  /**
   * 난방 조건 계산 (psychroChartData.ts의 로직을 가져옴)
   */
  private static calculateHeatingConditions(inputs: AHUInputs, elevation: number) {
    const heatingRoomTemp = inputs.heatingRoomTemp!;
    const heatingRoomHumidity = inputs.heatingRoomHumidity!;
    const heatingOutdoorTemp = inputs.heatingOutdoorTemp!;
    const heatingOutdoorHumidity = inputs.heatingOutdoorHumidity!;
    const supplyAirflow = inputs.supplyAirflow;
    const outdoorAirflow = inputs.outdoorAirflow;
    const heatingLoad = inputs.heatingLoad || 0;

    // RA Fan 위치 정보 가져오기 (100% 외기 시스템 판단용)
    let raFanPosition: 'None' | 'Inside' | 'Outside' = 'Inside';
    try {
      const fanSelectionData = localStorage.getItem('fan-selection-form-data');
      if (fanSelectionData) {
        const parsedData = JSON.parse(fanSelectionData);
        raFanPosition = parsedData.criteria?.raFanPosition || 'Inside';
      }
    } catch (error) {
      // 기본값 'Inside' 사용
    }

    // 1. 난방 외기 상태 (o2)
    const heatingOutdoorAir: AirState = PsychrometricCalculator.calculate({
      dryBulbTemp: heatingOutdoorTemp,
      relativeHumidity: heatingOutdoorHumidity,
      altitude: elevation
    })!;

    // 2. 난방 환기 상태 (r2)
    const heatingReturnAir: AirState = PsychrometricCalculator.calculate({
      dryBulbTemp: heatingRoomTemp,
      relativeHumidity: heatingRoomHumidity,
      altitude: elevation
    })!;

    // 3. 난방 혼합공기 상태 (m1) - RA Fan이 None이 아닐 때만 계산
    let heatingMixedAir: AirState | undefined;
    if (raFanPosition !== 'None' && supplyAirflow > 0 && outdoorAirflow >= 0) {
      // calculateMixedAir 함수를 사용하여 VBA Single 정밀도와 반올림 로직 적용
      heatingMixedAir = this.calculateMixedAir(heatingOutdoorAir, heatingReturnAir, inputs);
    }

    // 4. 난방 급기 상태 (l2) - 가습 후
    let heatingSupplyAir: AirState | undefined;
    let l2Temp: number;
    if (heatingLoad === 0) {
      l2Temp = heatingRoomTemp;
    } else {
      const temperatureRise = heatingLoad / (0.335 * supplyAirflow);
      l2Temp = heatingRoomTemp + Math.ceil(temperatureRise * 10) / 10;
    }
    try {
      const l2HumidityRatio = PsychrometricCalculator.calculateHumidityRatio(
        heatingRoomTemp, heatingRoomHumidity, elevation
      );
      const l2RH = PsychrometricCalculator.calculateRelativeHumidity(
        l2Temp, l2HumidityRatio, elevation
      );
      heatingSupplyAir = PsychrometricCalculator.calculate({
        dryBulbTemp: l2Temp,
        relativeHumidity: l2RH,
        altitude: elevation
      })!;
    } catch {
      const defaultHumidityRatio = PsychrometricCalculator.calculateHumidityRatio(
        heatingRoomTemp, heatingRoomHumidity, elevation
      ) || 0;
      heatingSupplyAir = PsychrometricCalculator.calculate({
        dryBulbTemp: l2Temp,
        humidityRatio: defaultHumidityRatio,
        altitude: elevation
      })!;
    }

    // 5. 난방 가열 후 상태 (l3)
    let heatingAfterHeating: AirState | undefined;
    if (supplyAirflow > 0 && outdoorAirflow >= 0 && heatingSupplyAir) {
      const l3Temp = heatingSupplyAir.dryBulbTemp; // l3 온도 = l2 온도
      try {
        let l3HumidityRatio: number;

        if (raFanPosition === 'None') {
          // 100% 외기 시스템: 외기 습도비 사용 (혼합 없음)
          // ASHRAE 난방 프로세스: o2 → l3 (현열 가열, 습도비 일정)
          l3HumidityRatio = PsychrometricCalculator.calculateHumidityRatio(
            heatingOutdoorTemp, heatingOutdoorHumidity, elevation
          );
        } else {
          // 환기 혼합 시스템: 혼합공기(m1) 습도비 사용
          const outdoorRatio = outdoorAirflow / supplyAirflow;
          const returnRatio = (supplyAirflow - outdoorAirflow) / supplyAirflow;
          const outdoorHumidityRatio = PsychrometricCalculator.calculateHumidityRatio(
            heatingOutdoorTemp, heatingOutdoorHumidity, elevation
          );
          const returnHumidityRatio = PsychrometricCalculator.calculateHumidityRatio(
            heatingRoomTemp, heatingRoomHumidity, elevation
          );
          l3HumidityRatio = (outdoorHumidityRatio * outdoorRatio) + (returnHumidityRatio * returnRatio);
        }

        const l3RH = PsychrometricCalculator.calculateRelativeHumidity(
          l3Temp, l3HumidityRatio, elevation
        );

        heatingAfterHeating = PsychrometricCalculator.calculate({
          dryBulbTemp: l3Temp,
          relativeHumidity: l3RH,
          altitude: elevation
        })!;
      } catch {
        const fallbackHumidityRatio = PsychrometricCalculator.calculateHumidityRatio(
          heatingRoomTemp, heatingRoomHumidity, elevation
        ) || 0;
        heatingAfterHeating = PsychrometricCalculator.calculate({
          dryBulbTemp: l3Temp,
          humidityRatio: fallbackHumidityRatio,
          altitude: elevation
        })!;
      }
    }

    return {
      heatingOutdoorAir,
      heatingReturnAir,
      heatingMixedAir,
      heatingSupplyAir,
      heatingAfterHeating
    };
  }

  /**
   * Supply Fan 계산 결과
   * Fan Selection 페이지와 연동하기 위한 계산값
   */
  private static calculateSupplyFanResults(inputs: AHUInputs, messages: string[]): {
    airflow: number;
    staticPressure: number;
    power: number;
    tempRise: number;
  } {
    const airflow = inputs.supplyAirflow; // 급기 풍량
    const tempRise = this.calculateSupplyFanTempRise(inputs); // SAte 계산
    
    // FAN 선정 탭에서 계산된 Supply Fan 정압을 우선적으로 사용
    let staticPressure = inputs.fanPressure || 935; // VBA 기대값: 935 Pa
    
    try {
      const fanSelectionData = localStorage.getItem('fan-selection-form-data');
      if (fanSelectionData) {
        const parsedData = JSON.parse(fanSelectionData);
        const calculatedStaticPressure = parsedData.supplyFan?.staticPressure;
        if (calculatedStaticPressure && calculatedStaticPressure > 0) {
          staticPressure = calculatedStaticPressure;
          messages.push(`Supply Fan 정압: FAN 선정 탭 계산값 ${staticPressure} Pa 사용`);
        } else {
          messages.push(`Supply Fan 정압: 기본값 ${staticPressure} Pa 사용 (FAN 선정 탭 값 없음)`);
        }
      }
    } catch (error) {
      messages.push(`Supply Fan 정압: 기본값 ${staticPressure} Pa 사용 (데이터 로드 실패)`);
    }
    
    let power = 0;
    if (inputs.hasFan && airflow > 0 && staticPressure > 0) {
      // Fan Selection Form에서 사용자가 입력한 값 가져오기
      let fanEfficiency = 60; // 기본값 (VBA TextBoxf19)
      let safetyFactor = 15; // 기본값
      
      try {
        const fanSelectionData = localStorage.getItem('fan-selection-form-data');
        if (fanSelectionData) {
          const parsedData = JSON.parse(fanSelectionData);
          fanEfficiency = parsedData.supplyFan?.efficiency || 60;
          safetyFactor = parsedData.supplyFan?.safetyFactor || 15;
        }
      } catch (error) {
        
      }
      
      
      // calculateFanPower 함수를 사용하여 동력 계산
      power = calculateFanPower(airflow, staticPressure, fanEfficiency, safetyFactor);
      
      messages.push(`Supply Fan 동력: ${power.toFixed(2)}kW (사용자 입력: ${fanEfficiency}%, ${safetyFactor}%)`);
    }
    
    return {
      airflow: PsychrometricCalculator.vbaRound(airflow, 0),
      staticPressure: PsychrometricCalculator.vbaRound(staticPressure, 0),
      power: PsychrometricCalculator.vbaRound(power, 2),
      tempRise: PsychrometricCalculator.vbaRound(tempRise, 2)
    };
  }

  /**
   * Return Fan 계산 결과
   * Fan Selection 페이지와 연동하기 위한 계산값
   */
  private static calculateReturnFanResults(inputs: AHUInputs, messages: string[]): {
    airflow: number;
    staticPressure: number;
    power: number;
  } {
    const airflow = inputs.returnAirflow || (inputs.supplyAirflow - inputs.outdoorAirflow);
    
    messages.push(`Return Fan 풍량: returnAirflow=${inputs.returnAirflow || 'null'}, 계산값=${inputs.supplyAirflow - inputs.outdoorAirflow} → 사용값=${airflow}CMH`);
     
    // FAN 선정 탭에서 계산된 Return Fan 정압을 우선적으로 사용
    let staticPressure = 311; // VBA 기대값으로 기본 설정
    
    try {
      const fanSelectionData = localStorage.getItem('fan-selection-form-data');
      if (fanSelectionData) {
        const parsedData = JSON.parse(fanSelectionData);
        const calculatedStaticPressure = parsedData.returnFan?.staticPressure;
        if (calculatedStaticPressure && calculatedStaticPressure > 0) {
          staticPressure = calculatedStaticPressure;
          messages.push(`Return Fan 정압: FAN 선정 탭 계산값 ${staticPressure} Pa 사용`);
        } else {
          messages.push(`Return Fan 정압: 기본값 ${staticPressure} Pa 사용 (FAN 선정 탭 값 없음)`);
        }
      }
    } catch (error) {
      messages.push(`Return Fan 정압: 기본값 ${staticPressure} Pa 사용 (데이터 로드 실패)`);
    }
    



    let power = 0;
    if (airflow > 0 && staticPressure > 0) {
      // Fan Selection Form에서 사용자가 입력한 값 가져오기
      let fanEfficiency = 50; // 기본값
      let safetyFactor = 15; // 기본값
      
      try {
        const fanSelectionData = localStorage.getItem('fan-selection-form-data');
        if (fanSelectionData) {
          const parsedData = JSON.parse(fanSelectionData);
          fanEfficiency = parsedData.returnFan?.efficiency || 50;
          safetyFactor = parsedData.returnFan?.safetyFactor || 15;
        }
      } catch (error) {
        
      }
      
      
      // calculateFanPower 함수를 사용하여 동력 계산
      power = calculateFanPower(airflow, staticPressure, fanEfficiency, safetyFactor);
      
      messages.push(`Return Fan 동력: ${power}kW (사용자 입력: ${fanEfficiency}%, ${safetyFactor}%)`);
    }
    
    return {
      airflow: PsychrometricCalculator.vbaRound(airflow, 0),
      staticPressure: PsychrometricCalculator.vbaRound(staticPressure, 0),
      power: PsychrometricCalculator.vbaRound(power, 2)
    };
  }
}


/**
 * VBA 'pwfuc' 함수의 로직을 TypeScript로 변환한 함수.
 * Excel 시트 'rtsdata'의 데이터에 의존합니다.
 *
 * @param vol 공기량 (vol&) - CMH
 * @param sp 정압 (Sp%) - Pa (파스칼)
 * @param ef 팬 효율 (ef%) - %
 * @param sf 안전율 (sf%) - %
 * @returns 계산된 동력(kW) 값
 */
export function calculateFanPower(
    vol: number,
    sp: number,
    ef: number,
    sf: number,
): number {
    // 원본 VBA 계산식: pw = vol * Sp / (3600000 * ef / 100) * (1 + sf / 100)
    const pw = (vol * sp) / (3600000 * ef / 100) * (1 + sf / 100);

    if (pw < 0.01 || pw >= 220) {
        return 0;
    }

    // 계산된 파워보다 실제 장비의 파워는 커야 할거야
    const result = rtsPwfucLookupData.find(value => value > pw) || 0;


    return PsychrometricCalculator.vbaRound(result, 2);
}